import { Navigate, BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChangePasswordPage,
  ForgotPasswordPage,
  LoginPage,
  OverviewPage,
  RegisterPage,
  BookingsPage,
  ToursPage,
  NewTourPage,
  EditTourPage,
  PostsPage,
  NewPostPage,
  EditPostPage,
  ReviewsPage,
  CountriesPage,
  NewCountryPage,
  EditCountryPage,
  CategoriesPage,
  NewCategoryPage,
  EditCategoryPage,
  SubscribersPage,
  AgentsPage,
  Pages,
  QuotesPage,
  LandingPages,
  NationalParksPage,
  NewNationalParkPage,
  EditNationalParkPage,
  AccommodationsPage,
  NewAccommodationPage,
  EditAccommodationPage,
  PlacesPage,
  NewPlacePage,
  EditPlacePage,
  StaffPage,
} from './components';
import { NotificationsPage, AuditPage } from './components/dashboard';
import ModernDashboard from './components/dashboard/ModernDashboard';
import RequestInterceptor from './adapters/axios';
import { AutoAuthenticate } from './store/actions';
import './App.css';
import { DAV_ROLES } from './constants';
import AgentOverview from './components/dashboard/agentPages/overview';
import CountryTours from './components/dashboard/agentPages/countryTours';
import Inquiries from './components/dashboard/pages/inquiries';
import DashboardLoader from './components/dashboard/sections/Loader/DashboardLoader';

const App = () => {
  RequestInterceptor();
  const { isLoggedIn: isAuth, user } = useSelector((state: any) => state.auth);
  const dispatch = useDispatch();
  const [isAuthCheckComplete, setIsAuthCheckComplete] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    AutoAuthenticate(dispatch);
    setIsAuthCheckComplete(true);
  }, [dispatch]);

  const userRouteRedirect = (role: string) => {
    if (window.location.pathname.startsWith('/dashboard')) {
      return window.location.pathname;
    }

    switch (role) {
      case DAV_ROLES.SUPER_ADMIN:
      case DAV_ROLES.ADMIN:
      case DAV_ROLES.AGENT:
        return '/dashboard/overview';

      case DAV_ROLES.TOUR_OPERATOR:
        return '/dashboard/tours';

      case DAV_ROLES.CONTENT_MANAGER:
        return '/dashboard/safari-updates';

      default:
        return '/dashboard/overview';
    }
  };

  if (!isAuthCheckComplete) {
    return (
      <div className='h-screen w-full flex items-center justify-center bg-gray-50 dark:bg-gray-900'>
        <DashboardLoader />
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path='/'
          element={isAuth ? <Navigate to={userRouteRedirect(user?.role)} /> : <LoginPage />}
        />
        <Route path='/register' element={<RegisterPage />} />
        <Route path='/forgot-password' element={<ForgotPasswordPage />} />
        <Route path='/reset-password/:token' element={<ChangePasswordPage />} />
        <Route path='/dashboard/*' element={isAuth ? <ModernDashboard /> : <Navigate to='/' />}>
          <Route
            path='overview'
            element={user?.role === DAV_ROLES.AGENT ? <AgentOverview /> : <OverviewPage />}
          />
          <Route path='bookings' element={<BookingsPage />} />
          <Route path='inquiries' element={<Inquiries />} />
          <Route path='quotes' element={<QuotesPage />} />
          <Route path='tours' element={<ToursPage />} />
          <Route path='tours/new' element={<NewTourPage />} />
          <Route path='tours/edit' element={<EditTourPage />} />
          <Route path='safari-updates' element={<PostsPage />} />
          <Route path='safari-updates/new' element={<NewPostPage />} />
          <Route path='safari-updates/edit' element={<EditPostPage />} />
          <Route path='reviews' element={<ReviewsPage />} />
          <Route path='countries' element={<CountriesPage />} />
          <Route path='countries/new' element={<NewCountryPage />} />
          <Route path='countries/edit' element={<EditCountryPage />} />
          <Route path='categories' element={<CategoriesPage />} />
          <Route path='categories/new' element={<NewCategoryPage />} />
          <Route path='categories/edit' element={<EditCategoryPage />} />
          <Route path='subscribers' element={<SubscribersPage />} />
          <Route path='notifications' element={<NotificationsPage />} />
          <Route path='agents' element={<AgentsPage />} />
          <Route path='pages' element={<Pages />} />
          <Route path='landing-pages' element={<LandingPages />} />
          <Route path='national-parks' element={<NationalParksPage />} />
          <Route path='national-parks/new' element={<NewNationalParkPage />} />
          <Route path='national-parks/edit' element={<EditNationalParkPage />} />
          <Route path='accommodations' element={<AccommodationsPage />} />
          <Route path='accommodations/new' element={<NewAccommodationPage />} />
          <Route path='accommodations/edit' element={<EditAccommodationPage />} />
          <Route path='places' element={<PlacesPage />} />
          <Route path='places/new' element={<NewPlacePage />} />
          <Route path='places/edit' element={<EditPlacePage />} />
          <Route path='staff' element={<StaffPage />} />
          <Route path='audit-log' element={<AuditPage />} />
          <Route path='tours/:countrySlug' element={<CountryTours />} />
        </Route>

        <Route path='*' element={<Navigate to='/' />} />
      </Routes>
    </Router>
  );
};

export default App;
